import { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../store/AppScopeProvider";
import {
  getBirthdayCouponsDetails,
  getAllConditionList,
  updateBirthdayCouponsV2,
} from "../api/BirthdayCouponsApi";
import { errorMessage, successMessage } from "../../../utils";
import { getRACTDetailsApi, updateRACTDetailsApi } from "../../ract";

export const useBirthdayCouponsV2Edit = ({ type = "birthdayCoupon" }) => {
  const isRACT = type === "ract";
  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const navigate = useNavigate();

  const [state, setState] = useImmer({
    isLoading: true,
    couponData: null,
    isSaveButtonBusy: false,
    httpStatusCode: null,
    ConditionList: [],
    isInProductRule: false,
    mobile_image: null,
    desktop_image: null,
  });

  useEffect(() => {
    fetchBirthdayCouponData();
    fetchConditionList();
  }, []);

  useEffect(() => {
    if (state.couponData && state.ConditionList.length > 0) {
      checkIsProductCondition();
    }
  }, [state.couponData?.conditions, state.ConditionList]);

  const fetchBirthdayCouponData = async () => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      const res = isRACT
        ? await getRACTDetailsApi()
        : await getBirthdayCouponsDetails();
      if (res.success) {
        setState((draft) => {
          draft.couponData = {
            ...res.data,
            // Convert conditions array to match regular coupons format
            conditions:
              res.data.conditions?.length > 0
                ? res.data.conditions.map((c) => ({ id: c.id, name: c.name }))
                : isRACT
                  ? []
                  : [{ id: "", name: "" }],
          };
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(
        err.response?.data?.message || "Failed to fetch coupon data",
      );
      setState((draft) => {
        draft.isLoading = false;
        draft.httpStatusCode = err.response?.status;
      });
    }
  };

  const fetchConditionList = async () => {
    try {
      const res = await getAllConditionList();
      if (res.success) {
        const filteredConditions = res.data?.filter((item) => item.scope !== 4 && item.scope !== 5) || [];
        setState((draft) => {
          draft.ConditionList = filteredConditions; // Filter BOGO/BXGY
        });
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || "Failed to fetch conditions");
    }
  };

  const updateRACT = async () => {
    setState((draft) => {
      draft.isSaveButtonBusy = true;
    });

    let formData = new FormData();

    try {
      const { couponData, mobile_image, desktop_image } = state;

      [
        "name",
        "description",
        "label",
        "id",
        "discount_amount",
        "discount_type",
        "condition_type",
        "applicable_max_product_qty",
      ].forEach((key) => {
        if (couponData[key] !== undefined) {
          formData.append(key, couponData[key]);
        }
      });

      if (couponData.conditions.length === 0) {
        formData.append("conditions[]", "");
      } else {
        couponData.conditions
          .filter((condition) => condition.id !== "" && condition.id != null)
          .forEach((condition) => {
            formData.append("conditions[]", JSON.stringify(condition.id));
          });
      }

      if (mobile_image) {
        formData.append("mobile_image", mobile_image);
      }
      if (desktop_image) {
        formData.append("desktop_image", desktop_image);
      }

      const response = await updateRACTDetailsApi(
        state.couponData.id,
        formData,
      );
      if (response.success) {
        navigate("/ract");
        successMessage(response.message);
      } else {
        throw new Error(response.message || "Failed to update RACT details");
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update RACT details";
      errorMessage(msg);
      console.error("Failed to update RACT details: ", error);
    } finally {
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  const handleInputChangeV2 = (e, index = null) => {
    // Handle dropdown change (e is the newValue object)
    if (index !== null && e && typeof e === "object" && "value" in e) {
      setState((draft) => {
        draft.couponData.conditions[index] = { id: e.value, name: e.label };
      });
      return;
    }

    const { name, value } = e.target;

    setState((draft) => {
      if (name === "applicable_max_product_qty") {
        // Handle numeric fields - convert to number or keep empty string
        draft.couponData[name] = value === "" ? "" : Number(value);
      } else if (
        name === "start_before_days" ||
        name === "end_before_days" ||
        name === "discount_amount"
      ) {
        // Handle other numeric fields
        draft.couponData[name] = value === "" ? "" : Number(value);
      } else {
        draft.couponData[name] = value;
      }
    });
  };

  const handleAddConditionV2 = (action, index = null) => {
    setState((draft) => {
      if (action === "add") {
        draft.couponData.conditions.push({ id: "", name: "" });
      } else if (action === "delete" && index !== null) {
        draft.couponData.conditions.splice(index, 1);
      }
    });
  };

  const handleDropzoneV2 = (files, name) => {
    const file = files[0]; // Dropzone passes array of files
    if (file) {
      setState((draft) => {
        if (name === "mobile_image") {
          draft.mobile_image = file;
          draft.couponData.mobile_image = URL.createObjectURL(file);
        } else if (name === "desktop_image") {
          draft.desktop_image = file;
          draft.couponData.desktop_image = URL.createObjectURL(file);
        }
      });
    }
  };

  const onUpdateBirthdayCouponV2 = async () => {
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });

      const formData = new FormData();

      // Append regular fields with null checks
      formData.append("name", state.couponData.name || "");
      formData.append("label", state.couponData.label || "");
      formData.append("description", state.couponData.description || "");
      formData.append(
        "start_before_days",
        state.couponData.start_before_days || 0,
      );
      formData.append("end_before_days", state.couponData.end_before_days || 0);
      formData.append("discount_type", state.couponData.discount_type || 1);
      formData.append("discount_amount", state.couponData.discount_amount || 0);
      formData.append("show_in_list", state.couponData.show_in_list ?? 1);
      formData.append("condition_type", state.couponData.condition_type || 1);
      formData.append("_method", "PUT");

      // Apply same conditional logic as regular coupons
      if (state.couponData.discount_type === 3 || state.isInProductRule) {
        formData.append(
          "applicable_max_product_qty",
          state.couponData.applicable_max_product_qty === null
            ? ""
            : state.couponData.applicable_max_product_qty,
        );
      } else {
        formData.append("applicable_max_product_qty", 0);
      }

      // Append conditions array
      const conditionIds = state.couponData.conditions
        .filter((c) => c.id !== "" && c.id !== null)
        .map((c) => parseInt(c.id));

      conditionIds.forEach((id) => {
        formData.append("conditions[]", id);
      });

      // Append images if they are File objects (newly uploaded)
      if (state.mobile_image) {
        formData.append("mobile_image", state.mobile_image);
      }
      if (state.desktop_image) {
        formData.append("desktop_image", state.desktop_image);
      }

      const res = await updateBirthdayCouponsV2(state.couponData.id, formData);
      if (res.success) {
        successMessage(res.message || "Birthday coupon updated successfully");
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
        navigate("/birthday-coupons-v2");
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || "Failed to update coupon");
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  const checkIsProductCondition = () => {
    if (
      !state.couponData?.conditions ||
      state.couponData.conditions.length === 0
    ) {
      setState((draft) => {
        draft.isInProductRule = false;
      });
      return;
    }

    const conditionIds = state.couponData.conditions
      .filter((c) => c.id !== "" && c.id !== null)
      .map((c) => Number(c.id))
      .filter((id) => !isNaN(id));

    const hasProductRule = state.ConditionList.some((v) => {
      return conditionIds.includes(v.id) && v.scope === 3;
    });

    setState((draft) => {
      draft.isInProductRule = hasProductRule;
      if (isRACT) {
        const qty = draft.couponData.applicable_max_product_qty;
        const hasQty = qty !== "" && qty !== null;
        if (hasProductRule && !hasQty) {
          draft.couponData.applicable_max_product_qty = "0";
        } else if (!hasProductRule) {
          draft.couponData.applicable_max_product_qty = "";
        }
      }
    });
  };

  return {
    isRACT,
    state,
    globalData,
    updateRACT,
    handleInputChangeV2,
    onUpdateBirthdayCouponV2,
    handleAddConditionV2,
    handleDropzoneV2,
  };
};
